import React, { useCallback, useState } from 'react';

import Card from 'react-bootstrap/Card';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { consultantDisplayName, invoiceAmountFormatter, niceDateFormatter, niceDateTimeFormatter, userNameFormatter } from '../../lib/helper/formatting';
import LedgerTab from './LedgerTab';
import { buildDuplicateVisibility, duplicateBadge } from './duplicates';
import InvoicePDFFileDownloadButton from '../../components/invoice/InvoicePDFFileDownloadButton';
import { ManageInvoiceContacts } from '../InvoiceParse/components/ManageInvoiceContacts';
import Alert from 'react-bootstrap/Alert';
import InvoiceAssignUserForm from '../../components/forms/Invoice/InvoiceAssignUserForm';
import InvoiceEscalationForm from '../../components/forms/Invoice/InvoiceEscalationForm';
import InvoiceAuditLog from '../../components/common/InvoiceAuditLog';
import { useSelector } from 'react-redux';
import InvoiceMissingDetailsForm from '../../components/forms/Invoice/InvoiceMissingDetailsForm';
import { canUploadInvoices, isAdmin, isChaserOutsourcer, isChaserOutsourcerPlus, isOutsourcer, isStaff } from '../../lib/helper/authorisation';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../lib/routing/frontend';
import { patientIdFromIri } from '../../lib/helper/patientFilters';
import InvoiceAdminActionsForm from '../../components/forms/Invoice/InvoiceAdminActionsForm';
import ManualInvoiceNotificationForm from '../../components/forms/Invoice/ManualInvoiceNotificationForm';
import InvoicePDFReplaceFileButton from '../../components/invoice/InvoicePDFReplaceFileButton';
import MASpinner from '../../components/common/MASpinner';
import InvoiceManualPaymentForm from '../../components/forms/Invoice/InvoiceManualPaymentForm';
import ManualPhoneCallForm from '../../components/forms/Invoice/ManualPhoneCallForm';
import UnlinkInvoiceFromBatchButton from '../../components/invoice/UnlinkInvoiceFromBatchButton';
import { requests } from '../../lib/helper/agent';
import * as API_ROUTES from '../../lib/routing/api';
import EmailPaymentReceiptForm from '../../components/invoice/EmailPaymentReceiptForm';
import { WorldpayStatuses } from '../../apiTypes';
import InvoiceNotes from '../../components/Invoice/InvoiceNotes';
import CreateInvoiceNoteForm from '../../components/forms/Invoice/CreateInvoiceNoteForm';
import PatientNotes from '../../components/Patient/PatientNotes';
import CreatePatientNoteForm from '../../components/forms/Patient/CreatePatientNoteForm';
import Badge from 'react-bootstrap/Badge';

const TabTitle = ({ text, count = null }) => (
  <span className="d-inline-flex align-items-center">
    {text}{' '}
    {count !== null ? (
      <Badge variant={'dark'} className={'ml-2'}>
        {count}
      </Badge>
    ) : (
      ''
    )}
  </span>
);

/**
 * A link in the invoice header - Batch, Patient, Consultant, Hospital.
 *
 * They all inherited the body colour with no underline, so nothing said any of them was
 * clickable. That was found by looking at a real invoice rather than at the markup: the
 * affordance simply does not read, and the patient link MED-921 adds would have been
 * undiscoverable.
 *
 * An underline rather than a colour, so the header keeps the muted look it has everywhere else.
 * See `.card-header__link` in cards.scss.
 *
 * One component rather than four copies of the class, so they cannot drift apart later.
 */
const HeaderLink = ({ to, title, children }) => (
  <Link to={to} title={title} className="card-header__link">
    {children}
  </Link>
);

const InvoiceSingle = ({
  invoice,
  addContactRecord,
  removeContactRecord,
  updateContactRecord,
  isUpdatingInvoice = false,
  invoiceNotes = [],
  patientNotes = [],
  canSeePatientNotes = false,
}) => {
  const [, setContactsUnderEdit] = useState(false);
  const [selectedPaymentRequestId, setSelectedPaymentRequestId] = useState(null);
  const [, setPaymentRequest] = useState(null);
  // MED-1023. Component state, so it resets with the page - opening any invoice starts with
  // duplicates hidden. `revealedDuplicatesFor` holds the payments whose marker has been clicked,
  // which is the only way to reveal anything: reveal is per group, so there is no global
  // "show all".
  const [revealedDuplicatesFor, setRevealedDuplicatesFor] = useState([]);
  const contactRecordTypes = useSelector((state) => state.contactRecordTypes.contactRecordTypes);
  const successfulStatuses = [WorldpayStatuses.AUTHORISED, WorldpayStatuses.CAPTURED, WorldpayStatuses.SETTLED];
  //  console.log({c: invoice.consultant});
  const isDirectPay =
    invoice.hospital?.id && invoice.consultant?.directPayHospitalIds && invoice.consultant?.directPayHospitalIds?.some((id) => id === invoice.hospital.id);

  const generatePaymentReceiptPdf = useCallback((paymentReq, invoice) => {
    requests.getFile(
      API_ROUTES.PAYMENT_RECEIPT_EXPORT.replace(':paymentRequestId', String(paymentReq?.id)),
      {},
      `payment-receipt-${invoice?.invoiceNo}-${niceDateTimeFormatter(paymentReq?.createdAt, 'YYYYMMDDHHmm')}.pdf`,
      true,
      true,
    );
  }, []);

  const onPaymentRequestSendSuccess = useCallback(() => {
    setPaymentRequest(null);
    setSelectedPaymentRequestId(null);
  }, []);

  if (!invoice) {
    return <MASpinner />;
  }
  const addNewContact = (newContact) => {
    addContactRecord({
      invoice: invoice['@id'],
      ...newContact,
      contactRecordType: contactRecordTypes.find((o) => o.code === newContact.contactRecordType.code)['@id'], // switch from object to iri
    });
    // invoice.contactRecords.push(newContact);
    return true;
  };
  const updateContact = (updatedContact, origContact) => {
    updateContactRecord(
      {
        ...updatedContact,
        contactRecordType: contactRecordTypes.find((o) => o.code === updatedContact.contactRecordType.code)['@id'], // switch from object to iri
      },
      updatedContact.id,
    );
    // const contacts = [...invoice.contactRecords];
    // const i = contacts.findIndex((v) => v.name === origContact.name);
    // contacts.splice(i, 1, updatedContact);
    // invoice.contactRecords = contacts;
    return true;
  };

  const removeContact = (contactToRemove) => {
    // console.log('InvoiceSingle.removeContact');
    removeContactRecord({
      invoice: invoice['@id'],
      ...contactToRemove,
    });
    return true;
  };

  const displayPaymentReceiptRow = (r) => {
    if (selectedPaymentRequestId !== r.id) {
      return null;
    }

    return (
      <tr>
        <td colSpan={7} className={'flex-grow-1'}>
          <EmailPaymentReceiptForm
            cancelSend={onPaymentRequestSendSuccess}
            form={`EmailPaymentReceiptForm-${r?.id}`}
            paymentRequest={r}
            onPaymentRequestSendSuccess={onPaymentRequestSendSuccess}
            invoiceContactRecords={invoice?.contactRecords}
          />
        </td>
      </tr>
    );
  };

  const hasPayments = invoice?.paymentRequests && invoice?.paymentRequests?.length > 0;

  // MED-966: a duplicate is a re-report of a payment already listed here - Healthcode's export
  // returning a card payment we took through the portal, over the phone or by hand. The row is
  // kept for the record but affects no balance, so it must not inflate the payment count either.
  const realPaymentCount = invoice?.paymentRequests?.filter((r) => !r?.isDuplicate)?.length ?? 0;
  const ledgerCount = invoice?.invoiceTransactions?.length ?? 0;

  // MED-1023. Which payment rows to render, and how many duplicates each original carries.
  // Decided here at render time rather than by filtering anything upstream: realPaymentCount
  // above already counts what it means to count, and the row list is the only thing changing.
  const paymentView = buildDuplicateVisibility(invoice?.paymentRequests, { revealedOriginalIds: revealedDuplicatesFor });

  const toggleDuplicatesFor = (id) => setRevealedDuplicatesFor((ids) => (ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]));

  // MED-921. Null when the invoice has no patient resolved yet - patient_id is nullable by
  // design - and the link is only offered to the internal roles that can actually follow it,
  // matching authByPath and Patient::READ_SECURITY on the API.
  const patientId = patientIdFromIri(invoice?.patient);
  const canViewPatient = isStaff() || isOutsourcer() || isChaserOutsourcer();

  return (
    <div>
      <Row>
        <Col>
          <Card className="mb-4">
            <Card.Header className={'card-header--invoice-batch-single'}>
              <Row>
                <Col md={3}>
                  <table>
                    <tbody>
                      <tr>
                        <th>Invoice:</th>
                        <td>{invoice.invoiceNo}</td>
                      </tr>
                      {invoice?.invoiceBatch?.id && (
                        <tr>
                          <th>Batch:</th>
                          <td>
                            <HeaderLink title="View Batch" to={ROUTES.INVOICE_BATCHES.SINGLE.replace(':id', String(invoice.invoiceBatch.id))}>
                              {invoice.invoiceBatch.id}
                            </HeaderLink>
                            {invoice.isVoid ? <UnlinkInvoiceFromBatchButton invoice={invoice} /> : null}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <th>{invoice.shortfallInvoiceDate ? 'Shortfall' : 'Invoice'} Date:</th>
                        <td>{niceDateFormatter(invoice.invoiceDate)}</td>
                      </tr>
                      {invoice.shortfallInvoiceDate && (
                        <tr>
                          <th>Original Invoice Date</th>
                          <td>{niceDateFormatter(invoice.shortfallInvoiceDate)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Col>
                <Col md={3}>
                  <table>
                    <tbody>
                      <tr>
                        <th>Patient:</th>
                        {/*
                          MED-921. The name shown is the invoice's own as-billed copy and stays
                          that way - the invoice must re-render as it was billed, and the register
                          holds current truth, which can differ. The link is the only thing added:
                          it goes to the person, where every invoice of theirs across every
                          consultant is in one place.

                          Absent when the invoice has no patient resolved yet (patient_id is
                          nullable by design) and when the reader is not internal, since
                          consultant-side users are refused every patient endpoint.
                        */}
                        <td>
                          {patientId && canViewPatient ? (
                            <HeaderLink to={ROUTES.PATIENTS.SINGLE.replace(':id', patientId)} title="View this patient's record">
                              {invoice.patientLastName + ', ' + invoice.patientFirstName}
                            </HeaderLink>
                          ) : (
                            invoice.patientLastName + ', ' + invoice.patientFirstName
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th>DOB:</th>
                        <td>{niceDateFormatter(invoice.dateOfBirth)}</td>
                      </tr>
                      {invoice.shortfallInvoiceDate && (
                        <tr>
                          <th></th>
                          <td>
                            <span className="ml-2 text-uppercase badge badge-pill badge-info">Shortfall</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/*<small className={'font-weight-normal'}>Patient:</small> {invoice.patientLastName + ', ' + invoice.patientFirstName}*/}
                </Col>
                <Col md={3}>
                  <table>
                    <tbody>
                      <tr>
                        <th>Consultant:</th>
                        <td>
                          <HeaderLink title="View Consultant" to={ROUTES.CONSULTANTS.SINGLE.replace(':id', String(invoice.consultant.id))}>
                            {consultantDisplayName(invoice.consultant)}
                          </HeaderLink>
                        </td>
                      </tr>

                      {invoice?.hospital?.id && (
                        <tr>
                          <th>Hospital:</th>
                          <td>
                            <HeaderLink title="View Hospital" to={ROUTES.HOSPITALS.SINGLE.replace(':id', String(invoice.hospital.id))}>
                              {invoice.hospital.name}
                            </HeaderLink>
                            {isDirectPay && (
                              <Link
                                title="View Consultant Hospital"
                                to={ROUTES.CONSULTANTS.SINGLE.replace(':id', String(invoice.consultant.id)) + '?tab=clinics'}>
                                <Badge variant={'primary ml-2'}>Direct Pay</Badge>
                              </Link>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Col>
                <Col md={2}>
                  <table>
                    <tbody>
                      <tr>
                        <th>O/S:</th>
                        <td>{invoiceAmountFormatter(invoice.outstandingBalance, '£')}</td>
                      </tr>

                      {isDirectPay && (
                        <tr>
                          <th>Client Account No:</th>
                          <td>{invoice.consultant.clientAccountNumber}</td>
                        </tr>
                      )}
                      {isDirectPay && (
                        <tr>
                          <th>Sort Code</th>
                          <td> {invoice.consultant.clientSortCode}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Col>
                <Col md={1}>
                  <table>
                    <tbody>
                      {invoice.isEscalated && (
                        <tr>
                          <th></th>
                          <td>
                            <span className="ml-2 text-uppercase badge badge-pill badge-danger">
                              <i className="fas fa-exclamation-circle" title="Escalated" />
                              <span className="ml-1 d-none d-sm-inline">Escalated</span>
                            </span>
                          </td>
                        </tr>
                      )}
                      {invoice.isVoid && (
                        <tr>
                          <th></th>
                          <td>
                            <span className="ml-2 text-uppercase badge badge-pill badge-dark">VOID</span>
                          </td>
                        </tr>
                      )}
                      {invoice.isPaid && !invoice.isVoid && (
                        <tr>
                          <th></th>
                          <td>
                            <span className="ml-2 text-uppercase badge badge-pill badge-primary">PAID</span>
                          </td>
                        </tr>
                      )}
                      {invoice.isWrittenOff && (
                        <tr>
                          <th></th>
                          <td>
                            <span className="ml-2 text-uppercase badge badge-pill badge-info">Written Off</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Col>
              </Row>
              {/*<Row className={'h5'}>*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  <small className={'font-weight-normal'}>{invoice.shortfallInvoiceDate ? 'Shortfall' : 'Invoice'} Date:</small>{' '}*!/*/}
              {/*  /!*  {niceDateFormatter(invoice.invoiceDate)}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  <small className={'font-weight-normal'}>DOB:</small> {niceDateFormatter(invoice.dateOfBirth)}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  {invoice?.hospital?.id && (*!/*/}
              {/*  /!*    <>*!/*/}
              {/*  /!*      <small className={'font-weight-normal mr-4'}>Hospital:</small>{' '}*!/*/}
              {/*  /!*      <Link to={ROUTES.HOSPITALS.SINGLE.replace(':id', String(invoice.hospital.id))}>{invoice.hospital.name}</Link>*!/*/}
              {/*  /!*      {isDirectPay && (*!/*/}
              {/*  /!*        <Link to={ROUTES.CONSULTANTS.SINGLE.replace(':id', String(invoice.consultant.id)) + '?tab=clinics'}>*!/*/}
              {/*  /!*          <Badge variant={'primary ml-2'}>Direct Pay</Badge>*!/*/}
              {/*  /!*        </Link>*!/*/}
              {/*  /!*      )}*!/*/}
              {/*  /!*    </>*!/*/}
              {/*  /!*  )}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  {isDirectPay && (*!/*/}
              {/*  /!*    <>*!/*/}
              {/*  /!*      <small className={'font-weight-normal'}>Client Account No:</small> {invoice.consultant.clientAccountNumber}*!/*/}
              {/*  /!*    </>*!/*/}
              {/*  /!*  )}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*</Row>*/}
              {/*<Row className={'h5'}>*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  {invoice.shortfallInvoiceDate ? (*!/*/}
              {/*  /!*    <>*!/*/}
              {/*  /!*      <small className={'font-weight-normal'}>Original Invoice Date:</small> {niceDateFormatter(invoice.shortfallInvoiceDate)}*!/*/}
              {/*  /!*    </>*!/*/}
              {/*  /!*  ) : null}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*  /!*<Col></Col>*!/*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  {invoice?.treatmentSetting?.id && (*!/*/}
              {/*  /!*    <>*!/*/}
              {/*  /!*      <small className={'font-weight-normal mr-3'}>T. Setting:</small>{' '}*!/*/}
              {/*  /!*      <Link to={ROUTES.HOSPITALS.SINGLE.replace(':id', String(invoice.treatmentSetting.id))}>{invoice.treatmentSetting.name}</Link>*!/*/}
              {/*  /!*    </>*!/*/}
              {/*  /!*  )}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*  /!*<Col>*!/*/}
              {/*  /!*  {' '}*!/*/}
              {/*  /!*  {isDirectPay && (*!/*/}
              {/*  /!*    <>*!/*/}
              {/*  /!*      <small className={'font-weight-normal'}>Sort Code:</small> {invoice.consultant.clientSortCode}*!/*/}
              {/*  /!*    </>*!/*/}
              {/*  /!*  )}*!/*/}
              {/*  /!*</Col>*!/*/}
              {/*</Row>*/}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={12} xl={3}>
                  <Alert variant={'gray text-dark'}>
                    <InvoiceAssignUserForm invoice={invoice} />
                    <hr />
                    <InvoiceEscalationForm invoice={invoice} />
                    <hr />
                    <InvoiceMissingDetailsForm invoice={invoice} isUpdatingInvoice={isUpdatingInvoice} />
                    <hr />
                    <Row className={'text-center'}>
                      <Col className={'col-12'}>
                        <InvoicePDFFileDownloadButton invoice={invoice} />
                      </Col>
                      {(isStaff() || canUploadInvoices()) && (
                        <Col className={'col-12'}>
                          <InvoicePDFReplaceFileButton invoice={invoice} />
                        </Col>
                      )}
                    </Row>
                  </Alert>
                  {(isStaff() || isChaserOutsourcer()) && invoice && (
                    <Row>
                      <Col>
                        {!!invoice?.invoicePDFFile?.id && invoice.contactRecords.filter((c) => c.notifyByEmail || c.notifyByMobile).length > 0 && (
                          <Alert variant={'gray text-dark'}>
                            <ManualInvoiceNotificationForm invoice={invoice} />
                          </Alert>
                        )}
                        <Alert variant={'gray text-dark'}>
                          <h5>Manual Phone Call</h5>
                          <ManualPhoneCallForm invoice={invoice} isUpdatingInvoice={isUpdatingInvoice} />
                        </Alert>
                      </Col>
                    </Row>
                  )}
                  {(isStaff() || isChaserOutsourcerPlus(true)) && invoice && (
                    <Row>
                      <Col>
                        <Alert variant={'gray text-dark'}>
                          <h5>Invoice Actions</h5>
                          {invoice.outstandingBalance > 0 && !invoice.isPaid && !invoice.isWrittenOff && (
                            <>
                              <InvoiceManualPaymentForm invoice={invoice} isUpdatingInvoice={isUpdatingInvoice} />
                              <hr />
                            </>
                          )}
                          <InvoiceAdminActionsForm invoice={invoice} />
                        </Alert>
                      </Col>
                    </Row>
                  )}
                </Col>
                <Col xl={9}>
                  <ManageInvoiceContacts
                    payorTypeCode={invoice?.payorType?.code}
                    setContactsUnderEdit={setContactsUnderEdit}
                    contacts={invoice.contactRecords}
                    updateContact={updateContact}
                    addNewContact={addNewContact}
                    removeContact={removeContact}
                    existingInvoice={true}
                  />
                  <hr />
                  <p>
                    Notification Status:
                    {invoice.disableChasing && <i className="ml-2 fas fa-pause-circle text-danger" title="Notifications Paused" />}
                    {invoice.hasOwnProperty('shouldSendNotifications') && invoice.shouldSendNotifications === true ? (
                      <i className="ml-2 fas fa-play-circle text-primary" title="Notifications should currently send" />
                    ) : (
                      <span className={'ml-2 bg-danger text-white p-2'}>
                        <i className="mr-2 fas fa-pause-circle text-white" title="Notifications should not send" />
                        Notifications currently wont send: {invoice.shouldSendNotifications}
                      </span>
                    )}
                  </p>
                  <Tabs defaultActiveKey={hasPayments ? 'payments' : 'notifications'} variant="pills">
                    {hasPayments ? (
                      <Tab eventKey="payments" title={<TabTitle text={'Payment Activity'} count={realPaymentCount > 0 ? realPaymentCount : null} />}>
                        <Alert variant={'success'}>
                          <Row className={'d-flex flex-grow-1'}>
                            <table className={'table-sm flex-grow-1'}>
                              <tbody>
                                {paymentView.rows.map((r) => {
                                  // MED-1023. Read from the flags rather than inferred from the
                                  // link. This tab used to call a flagged duplicate with no
                                  // duplicateOfId "needs review", which PaymentRequest cannot
                                  // know - it has no awaiting-review field, only isDuplicate and
                                  // duplicateOf. A missing link is a link we could not make, not
                                  // a decision nobody has taken.
                                  const { badge, badgeTitle } = duplicateBadge(r, { noun: 'payment' });

                                  return (
                                    <React.Fragment key={r.id}>
                                      <tr className={badge ? 'text-muted' : undefined}>
                                        <th>
                                          {r.paymentProvider === 'manual' ? 'Manual Payment' : 'Payment Request'}
                                          {badge ? (
                                            <Badge variant={'secondary ml-2'} title={badgeTitle}>
                                              {badge}
                                            </Badge>
                                          ) : null}
                                          {/* With the duplicate rows gone this marker is the only
                                              thing on the screen saying a re-report exists, and
                                              the only control that can bring those rows back -
                                              there is no global "show all". That is why
                                              duplicates.js refuses to hide a row with no marker
                                              to return it. */}
                                          {r.duplicateCount > 0 ? (
                                            <Badge
                                              variant={'secondary'}
                                              className={'ml-2 pointer'}
                                              title={`This payment was re-reported ${r.duplicateCount === 1 ? 'once' : `${r.duplicateCount} times`}. Click to ${
                                                r.duplicatesRevealed ? 'hide' : 'show'
                                              } the re-reports; they do not affect the balance.`}
                                              onClick={() => toggleDuplicatesFor(r.id)}>
                                              {r.duplicateMarker}
                                            </Badge>
                                          ) : null}
                                        </th>
                                        <td className={'px-4'}>{niceDateTimeFormatter(r.createdAt)}</td>
                                        <td className={'px-4'}>
                                          {r.paymentProvider !== 'manual' ? 'Ref: ' : ''}
                                          {r.transactionReference} {r.paymentProvider !== 'manual' ? `(${r.paymentProvider})` : ''}
                                        </td>
                                        <td className={'px-4'}>Amount: {invoiceAmountFormatter(r.amount, '£')}</td>
                                        <td>{r.paymentProvider !== 'manual' ? `Status: ${r.status.name}` : ''}</td>
                                        <td>
                                          {r.paymentProvider !== 'manual' ? (
                                            r?.transactionId ? (
                                              `Transaction ID: ${r.transactionId}`
                                            ) : (
                                              <div dangerouslySetInnerHTML={{ __html: String(r.note).replace(/ - /g, '<br/>') }} />
                                            )
                                          ) : (
                                            ''
                                          )}
                                        </td>
                                        <td className="d-flex" style={{ justifyContent: 'end' }}>
                                          {/* No receipt for a duplicate - the money it describes was receipted against the original. */}
                                          {successfulStatuses.includes(r.status.code) && !r?.isDuplicate ? (
                                            <>
                                              <span
                                                title={`Export payment receipt for ${r.transactionReference}`}
                                                onClick={() => generatePaymentReceiptPdf(r, invoice)}>
                                                <i className="p-2 fas fa-file-download pointer" />
                                              </span>
                                              <span
                                                title={`Email payment receipt for ${r.transactionReference}`}
                                                onClick={() => {
                                                  const newId = selectedPaymentRequestId === r.id ? null : r.id;
                                                  setSelectedPaymentRequestId(newId);
                                                  setPaymentRequest(newId ? r : null);
                                                }}>
                                                <i className="p-2 mx-2 fas fa-envelope pointer" />
                                              </span>
                                            </>
                                          ) : null}
                                        </td>
                                      </tr>
                                      {displayPaymentReceiptRow(r)}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Row>
                        </Alert>
                      </Tab>
                    ) : null}
                    {/* MED-967/969. Sits alongside Payment Activity rather than replacing it:
                        during phase 1 the ledger is written in parallel with the balance and
                        read by nothing, so showing both is what lets staff who know the invoice
                        confirm the two agree. MED-975 makes this the primary view.

                        Always rendered, even with no rows. The backfill opens a ledger only for
                        invoices that are owed something, so most invoices have none - and a tab
                        that appears on one invoice in twelve reads as a bug. LedgerTab explains
                        the empty case, and flags it when an invoice is owed money and the ledger
                        cannot account for it. */}
                    <Tab eventKey="ledger" title={<TabTitle text={'Ledger'} count={ledgerCount > 0 ? ledgerCount : null} />}>
                      <LedgerTab invoice={invoice} />
                    </Tab>
                    <Tab
                      eventKey="notifications"
                      title={
                        <TabTitle
                          text={'Notification Activity'}
                          count={invoice?.invoiceNotifications?.length > 0 ? invoice?.invoiceNotifications?.length : null}
                        />
                      }>
                      <Alert variant={'info'}>
                        <Row>
                          <Col>
                            <div>
                              <table className={'table-sm'}>
                                <tbody>
                                  <tr>
                                    <th>Record Created</th>
                                    <td className={'px-4'}>{niceDateTimeFormatter(invoice.createdAt)}</td>
                                    <td className={'px-4'}>{invoice.createdBy ? <>by {userNameFormatter(invoice.createdBy)}</> : null}</td>
                                    <td colSpan={2}></td>
                                  </tr>
                                  {invoice?.invoiceNotifications?.map((n) =>
                                    n.events && n.events.length > 0 ? (
                                      n.events.map((e, i) => (
                                        <tr key={e.id} data-notif-id={n.id} data-event-id={e.id}>
                                          <th>{i === 0 ? n.activityName : ''}</th>
                                          <td className={'px-4'}>
                                            {String(e?.sentAt) !== '' && String(e?.sentAt) !== 'undefined'
                                              ? niceDateTimeFormatter(e.sentAt)
                                              : niceDateTimeFormatter(e.createdAt)}
                                          </td>
                                          <td className={'px-4'}>
                                            {e.type.name}{' '}
                                            {e.type.name === 'Phone Call'
                                              ? 'made'
                                              : `sent to ${e.sentTo ? e.sentTo + ' ' : ''}${e.contactRecord?.contactRecordType?.name ?? ''}`}
                                          </td>
                                          <td className={'px-4'}>Status:{' ' + String(e.status)}</td>
                                          <td>
                                            {e.type.name === 'Whatsapp' && e.serviceIdentifier ? (
                                              isAdmin() ? (
                                                <a
                                                  href={`https://console.twilio.com/us1/monitor/logs/sms?pageSize=50&sid=${e.serviceIdentifier}`}
                                                  rel="noreferrer"
                                                  target={'_blank'}>
                                                  SID: {e.serviceIdentifier}
                                                </a>
                                              ) : (
                                                e.serviceIdentifier
                                              )
                                            ) : (
                                              ''
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr key={n.id} data-notif-id={n.id}>
                                        <th>{n.activityName}</th>
                                        <td className={'px-4'}>{niceDateTimeFormatter(n.sentAt)}</td>
                                        <td className={'px-4'}>
                                          Part of batch email
                                          {n.primaryInvoiceId ? (
                                            <>
                                              {' '}
                                              (see <Link to={ROUTES.INVOICES.SINGLE.replace(':id', n.primaryInvoiceId)}>related invoice</Link>)
                                            </>
                                          ) : null}
                                        </td>
                                        <td className={'px-4'}>-</td>
                                        <td></td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                        </Row>
                      </Alert>
                    </Tab>
                    {/*
                      Named for what it holds, now that Patient Notes sits beside it. "Notes"
                      alone stopped being an answer the moment there were two kinds.
                    */}
                    <Tab eventKey="notes" title={<TabTitle text={'Invoice Notes'} count={invoiceNotes?.length > 0 ? invoiceNotes?.length : null} />}>
                      <Row>
                        <Col md={8}>
                          <InvoiceNotes invoice={invoice} />
                        </Col>
                        <Col md={4}>
                          <CreateInvoiceNoteForm invoice={invoice} />
                        </Col>
                      </Row>
                    </Tab>
                    {/*
                      MED-922. A separate tab, deliberately. The tab above is notes about THIS
                      invoice; this one is notes about the PERSON, shared across every consultant
                      who sees them. A note written here is a PatientNote and lands on the
                      register, not on the invoice - the existing form above must never quietly
                      become the patient one.

                      Only mounted for internal users. Consultant-side users are refused the
                      endpoint outright, and a tab that loads and then cannot fetch would only
                      advertise something they are not meant to know exists.
                    */}
                    {canSeePatientNotes && patientId && (
                      <Tab eventKey="patientNotes" title={<TabTitle text={'Patient Notes'} count={patientNotes?.length > 0 ? patientNotes?.length : null} />}>
                        <Row>
                          <Col md={8}>
                            <PatientNotes patientId={patientId} />
                          </Col>
                          <Col md={4}>
                            <CreatePatientNoteForm patientId={patientId} form={`CreatePatientNoteForm_${patientId}`} />
                          </Col>
                        </Row>
                      </Tab>
                    )}
                  </Tabs>
                </Col>
              </Row>

              <hr />
              <Row>
                <Col>
                  <div className="crud__section mb-3">
                    <div className="crud__section-icon">
                      <i className="fas fa-book" />
                    </div>
                    <h2 className="crud__section-header">Audit Log</h2>
                    <InvoiceAuditLog invoice={invoice} />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/*{jspp({ invoice })}*/}
    </div>
  );
};

export default InvoiceSingle;
